import React, { useState, useEffect, useRef } from "react";
import { Container, Row, Col, Spinner, Modal } from "react-bootstrap";
import axiosClient from "../api/axiosClient";

const UserManagement = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [activeSubscribers, setActiveSubscribers] = useState([]);
  const [loadingSubscribers, setLoadingSubscribers] = useState(false);
  const [showAllSubscribers, setShowAllSubscribers] = useState(false);
  const [oneTimeBuyers, setOneTimeBuyers] = useState([]);
  const [loadingOneTime, setLoadingOneTime] = useState(false);
  const [showOneTime, setShowOneTime] = useState(false);
  const [oneTimeFilter, setOneTimeFilter] = useState("");
  // Server-side paging. The whole list used to be fetched and rendered at once,
  // which took the browser down once the funnel passed a few hundred buyers.
  const ONE_TIME_PAGE_SIZE = 50;
  const [oneTimeOffset, setOneTimeOffset] = useState(0);
  const [oneTimeTotal, setOneTimeTotal] = useState(0);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetails, setUserDetails] = useState(null);
  const [userProfiles, setUserProfiles] = useState([]);
  const [userBoosts, setUserBoosts] = useState([]);
  const [userSubscriptions, setUserSubscriptions] = useState([]);
  const [userTopups, setUserTopups] = useState(null);
  const [balanceAmount, setBalanceAmount] = useState("");
  const [balanceNote, setBalanceNote] = useState("");
  const [savingBalance, setSavingBalance] = useState(false);
  const [balanceError, setBalanceError] = useState("");
  const [activeTab, setActiveTab] = useState("details");
  const [showEditUserModal, setShowEditUserModal] = useState(false);
  const [showEditProfileModal, setShowEditProfileModal] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [editUserForm, setEditUserForm] = useState({ email: "", fullName: "", balance: 0, affiliate: "" });
  const [editProfileForm, setEditProfileForm] = useState({
    platform: "", username: "", platform_id: "", planName: "",
    interests: "", ageRange: "", genderTarget: "", countries: "",
    followers: 0, subscriptionid: null,
  });

  const debounceRef = useRef(null);

  // The filter is applied by the server now, so it searches every buyer rather
  // than whichever page happens to be loaded.
  const filteredOneTime = oneTimeBuyers;

  const handleSearch = async (query) => {
    const q = query !== undefined ? query : searchQuery;
    if (!q.trim()) { setSearchResults([]); return; }
    setSearching(true);
    setSelectedUser(null);
    // Both browse lists have to close, not just one. The results table below
    // renders behind `!showAllSubscribers && !showOneTime`, so leaving either
    // flag set makes a search return its rows and display nothing — which is
    // what searching from the one-time list did.
    setShowAllSubscribers(false);
    setShowOneTime(false);
    try {
      const response = await axiosClient.get(`https://api.viralgpt.com/admin/users/search?query=${encodeURIComponent(q)}`);
      setSearchResults(response.data);
    } catch (error) {
      console.error("Error searching users:", error);
    } finally {
      setSearching(false);
    }
  };

  useEffect(() => {
    if (!searchQuery.trim()) { setSearchResults([]); return; }
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => handleSearch(searchQuery), 350);
    return () => clearTimeout(debounceRef.current);
  }, [searchQuery]);

  const handleSelectUser = async (userId) => {
    setSelectedUser(userId);
    setUserDetails(null);
    setUserProfiles([]);
    setUserBoosts([]);
    setUserSubscriptions([]);
    setUserTopups(null);
    setBalanceAmount("");
    setBalanceNote("");
    setBalanceError("");
    setActiveTab("details");
    await fetchUserDetails(userId);
  };

  const handleBackToSearch = () => {
    setSelectedUser(null);
    setUserDetails(null);
    setUserProfiles([]);
    setUserBoosts([]);
    setUserSubscriptions([]);
  };

  const fetchUserDetails = async (userId) => {
    try {
      const response = await axiosClient.get(`https://api.viralgpt.com/admin/users/${userId}`);
      setUserDetails(response.data);
      setEditUserForm({
        email: response.data.email || "",
        fullName: response.data.fullName || "",
        balance: response.data.balance || 0,
        affiliate: response.data.affiliate || "",
      });
      fetchUserProfiles(userId);
      fetchUserBoosts(userId);
      fetchUserSubscriptions(userId);
      fetchUserTopups(userId);
    } catch (error) { console.error("Error fetching user details:", error); }
  };

  const fetchUserProfiles = async (userId) => {
    try {
      const response = await axiosClient.get(`https://api.viralgpt.com/admin/users/${userId}/profiles`);
      setUserProfiles(response.data.map((p) => ({ ...p, created_at: p.created_at || p.timestamp })));
    } catch (error) { console.error("Error fetching user profiles:", error); }
  };

  const fetchUserBoosts = async (userId) => {
    try {
      const response = await axiosClient.get(`https://api.viralgpt.com/admin/users/${userId}/boosts`);
      setUserBoosts(response.data);
    } catch (error) { console.error("Error fetching user boosts:", error); }
  };

  const fetchUserTopups = async (userId) => {
    try {
      const response = await axiosClient.get(`https://api.viralgpt.com/admin/users/${userId}/topups`);
      setUserTopups(response.data);
    } catch (error) { console.error("Error fetching user top-ups:", error); }
  };

  const handleAdjustBalance = async () => {
    const amount = parseFloat(balanceAmount);
    if (!Number.isFinite(amount) || amount === 0) {
      setBalanceError("Enter an amount other than zero.");
      return;
    }
    setSavingBalance(true);
    setBalanceError("");
    try {
      await axiosClient.post(`https://api.viralgpt.com/admin/users/${selectedUser}/balance`, {
        amount, note: balanceNote,
      });
      setBalanceAmount("");
      setBalanceNote("");
      // Both, because the header and the Details tab read the balance off the
      // user record while this tab reads it off the top-up response.
      fetchUserTopups(selectedUser);
      fetchUserDetails(selectedUser);
    } catch (error) {
      setBalanceError(error.response?.data?.error || "Could not adjust the balance.");
    } finally { setSavingBalance(false); }
  };

  const fetchUserSubscriptions = async (userId) => {
    try {
      const response = await axiosClient.get(`https://api.viralgpt.com/admin/users/${userId}/subscriptions`);
      setUserSubscriptions(response.data);
    } catch (error) { console.error("Error fetching user subscriptions:", error); }
  };

  const fetchAllActiveSubscribers = async () => {
    if (showAllSubscribers) { setShowAllSubscribers(false); return; }
    setLoadingSubscribers(true);
    try {
      const response = await axiosClient.get(`https://api.viralgpt.com/admin/users/active-subscribers`);
      setActiveSubscribers(response.data);
      setShowAllSubscribers(true);
      setSelectedUser(null);
    } catch (error) { console.error("Error fetching active subscribers:", error); }
    finally { setLoadingSubscribers(false); }
  };

  const lastOneTimeQuery = useRef("");
  const loadOneTimePage = async (offset, q) => {
    lastOneTimeQuery.current = (q || "").trim();
    setLoadingOneTime(true);
    try {
      const response = await axiosClient.get(
        `https://api.viralgpt.com/admin/users/one-time?limit=${ONE_TIME_PAGE_SIZE}&offset=${offset}` +
        (q && q.trim() ? `&q=${encodeURIComponent(q.trim())}` : '')
      );
      // Tolerates the old array response so a frontend deployed ahead of the
      // backend still renders instead of blanking.
      const body = response.data;
      const rows = Array.isArray(body) ? body : (body.rows || []);
      setOneTimeBuyers(rows);
      setOneTimeTotal(Array.isArray(body) ? rows.length : (Number(body.total) || 0));
      setOneTimeOffset(offset);
    } catch (error) { console.error("Error fetching one-time buyers:", error); }
    finally { setLoadingOneTime(false); }
  };

  const fetchOneTimeBuyers = async () => {
    // Closing the list drops its filter too, so reopening never comes back
    // pre-filtered to something the admin has forgotten they typed.
    if (showOneTime) { setShowOneTime(false); setOneTimeFilter(""); setOneTimeOffset(0); return; }
    await loadOneTimePage(0, "");
    setShowOneTime(true);
    setShowAllSubscribers(false);
    setSelectedUser(null);
  };

  // Typing in the filter re-queries from the first page.
  const oneTimeDebounceRef = useRef(null);
  useEffect(() => {
    if (!showOneTime) return;
    // Opening the list already loaded page one for this query; only a genuine
    // change of what was typed should re-query.
    if (oneTimeFilter.trim() === lastOneTimeQuery.current) return;
    clearTimeout(oneTimeDebounceRef.current);
    oneTimeDebounceRef.current = setTimeout(() => loadOneTimePage(0, oneTimeFilter), 350);
    return () => clearTimeout(oneTimeDebounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [oneTimeFilter, showOneTime]);

  const handleSaveUserChanges = async () => {
    try {
      await axiosClient.put(`https://api.viralgpt.com/admin/users/${selectedUser}`, editUserForm);
      setShowEditUserModal(false);
      fetchUserDetails(selectedUser);
    } catch (error) { console.error("Error updating user:", error); }
  };

  const handleEditProfile = (profile) => {
    setSelectedProfile(profile);
    setEditProfileForm({
      platform: profile.platform || "", username: profile.username || "",
      platform_id: profile.platform_id || "", planName: profile.planName || "",
      interests: profile.interests || "", ageRange: profile.ageRange || "",
      genderTarget: profile.genderTarget || "", countries: profile.countries || "",
      followers: profile.followers || 0, subscriptionid: profile.subscriptionid || null,
    });
    setShowEditProfileModal(true);
  };

  const handleSaveProfileChanges = async () => {
    try {
      await axiosClient.put(`https://api.viralgpt.com/admin/users/${selectedUser}/profiles/${selectedProfile.profileid}`, editProfileForm);
      setShowEditProfileModal(false);
      fetchUserProfiles(selectedUser);
    } catch (error) { console.error("Error updating profile:", error); }
  };

  const handleDeleteProfile = async (profileId) => {
    if (window.confirm("Are you sure you want to delete this profile?")) {
      try {
        await axiosClient.delete(`https://api.viralgpt.com/admin/users/${selectedUser}/profiles/${profileId}`);
        fetchUserProfiles(selectedUser);
        fetchUserDetails(selectedUser);
      } catch (error) { console.error("Error deleting profile:", error); }
    }
  };

  const handleUpdateSubscriptionStatus = async (subscriptionId, newStatus) => {
    try {
      await axiosClient.put(`https://api.viralgpt.com/admin/subscriptions/${subscriptionId}`, { status: newStatus });
      fetchUserSubscriptions(selectedUser);
    } catch (error) { console.error("Error updating subscription status:", error); }
  };

  const handleLoginAsUser = async () => {
    try {
      const response = await axiosClient.post(`https://api.viralgpt.com/admin/users/${selectedUser}/login-as-user`);
      localStorage.setItem("accessToken", response.data.accessToken);
      const newWindow = window.open('/dashboard', '_blank');
      if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
        window.location.href = '/dashboard';
      }
    } catch (error) { console.error("Error logging in as user:", error); }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const statusColor = {
    active: 'green', completed: 'green', paid: 'green',
    paused: 'yellow', pending: 'yellow', processing: 'yellow',
    cancelled: 'red', canceled: 'red', failed: 'red',
    skipped: 'gray',
  };

  const planColor = { pro: 'purple', basic: 'blue', free: 'gray' };

  // ─── Search View ────────────────────────────────────────────────────────
  const renderUserSearch = () => (
    <div className="stripe-card--sm" style={{ overflow: 'hidden' }}>
      <div style={{ padding: '1.25rem 1.5rem' }}>
        <div className="stripe-label stripe-text-muted" style={{ marginBottom: '1rem' }}>User Search</div>

        <div className="d-flex gap-2 mb-3">
          <input
            className="stripe-input"
            type="text"
            placeholder="Search by email, username, user ID, or card number..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { clearTimeout(debounceRef.current); handleSearch(searchQuery); } }}
          />
          <button className="stripe-btn stripe-btn--primary" onClick={() => handleSearch(searchQuery)} disabled={searching} style={{ whiteSpace: 'nowrap', padding: '8px 20px' }}>
            {searching ? <Spinner animation="border" size="sm" /> : "Search"}
          </button>
        </div>

        <div className="d-flex justify-content-end mb-3">
          <button
            className={`stripe-btn stripe-btn--outline`}
            onClick={fetchAllActiveSubscribers}
            disabled={loadingSubscribers}
            style={{ padding: '8px 18px', fontSize: '0.85rem' }}
          >
            {loadingSubscribers ? <Spinner animation="border" size="sm" /> : showAllSubscribers ? "Hide Subscribers" : "Show All Subscribers"}
          </button>
          <button
            className={`stripe-btn stripe-btn--outline ms-2`}
            onClick={fetchOneTimeBuyers}
            disabled={loadingOneTime}
            style={{ padding: '8px 18px', fontSize: '0.85rem' }}
          >
            {loadingOneTime ? <Spinner animation="border" size="sm" /> : showOneTime ? "Hide one-time" : "Show all one-time"}
          </button>
        </div>

        {/* One-time package buyers. Delivery is shown per row because a charge
            without a delivery is the failure mode worth spotting here. */}
        {showOneTime && (
          <>
            <div className="stripe-label stripe-text-muted" style={{ marginTop: '0.5rem', marginBottom: '0.75rem' }}>
              One-Time Buyers <span className="stripe-text-dark" style={{ fontWeight: 700 }}>
                ({oneTimeTotal.toLocaleString()}{oneTimeFilter.trim() ? ' matching' : ''})
              </span>
            </div>
            {/* Queries the server across every buyer, not just this page. The
                reason to look someone up here rather than in the search above
                is the Paid/Delivered columns, so the lookup has to keep them
                on screen. */}
            <input
              className="stripe-input mb-3"
              type="text"
              placeholder="Filter these buyers by email or username..."
              value={oneTimeFilter}
              onChange={(e) => setOneTimeFilter(e.target.value)}
            />
            {filteredOneTime.length === 0 && oneTimeFilter.trim() ? (
              <div className="stripe-text-muted" style={{ fontSize: '0.85rem' }}>No one-time buyer matches “{oneTimeFilter}”.</div>
            ) : filteredOneTime.length === 0 ? (
              <div className="stripe-text-muted" style={{ fontSize: '0.85rem' }}>No one-time purchases yet.</div>
            ) : (
            <div style={{ overflowX: 'auto' }}>
              <table className="stripe-table">
                <thead>
                  <tr>
                    <th>Email</th>
                    <th className="d-none d-md-table-cell">Profile</th>
                    <th>Paid</th>
                    <th className="d-none d-md-table-cell">Delivered</th>
                    <th className="d-none d-md-table-cell">Date</th>
                    <th style={{ width: '1%' }}></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredOneTime.map((u, i) => (
                    <tr key={`${u.userid}-${i}`}>
                      <td>
                        <div className="stripe-text-dark" style={{ fontWeight: 600, fontSize: '0.85rem' }}>{u.email}</div>
                        <div className="stripe-text-muted d-md-none" style={{ fontSize: '0.75rem' }}>@{u.username || '—'}</div>
                      </td>
                      <td className="d-none d-md-table-cell">@{u.username || '—'}</td>
                      <td style={{ fontWeight: 600 }}>${u.price}</td>
                      <td className="d-none d-md-table-cell">
                        {Number(u.undelivered) > 0 ? (
                          <span className="stripe-badge" style={{ background: '#fef9c3', color: '#a16207' }}>Owed</span>
                        ) : Number(u.deliveries) > 0 ? (
                          <span className="stripe-badge stripe-badge--green">{Number(u.followers_sent || 0).toLocaleString()}</span>
                        ) : (
                          <span className="stripe-badge" style={{ background: '#fee2e2', color: '#b91c1c' }}>None</span>
                        )}
                      </td>
                      <td className="d-none d-md-table-cell">{u.createdAt ? formatDate(u.createdAt) : "—"}</td>
                      <td style={{ textAlign: 'right' }}>
                        <button className="stripe-btn--sm stripe-btn--sm-primary" onClick={() => handleSelectUser(u.userid)}>View</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            )}
            {oneTimeTotal > ONE_TIME_PAGE_SIZE && (
              <div className="d-flex align-items-center justify-content-between mt-3">
                <button
                  className="stripe-btn--sm"
                  disabled={oneTimeOffset === 0 || loadingOneTime}
                  onClick={() => loadOneTimePage(Math.max(0, oneTimeOffset - ONE_TIME_PAGE_SIZE), oneTimeFilter)}
                >
                  ← Previous
                </button>
                <span className="stripe-text-muted" style={{ fontSize: '0.8rem' }}>
                  {(oneTimeOffset + 1).toLocaleString()}–{Math.min(oneTimeOffset + filteredOneTime.length, oneTimeTotal).toLocaleString()} of {oneTimeTotal.toLocaleString()}
                </span>
                <button
                  className="stripe-btn--sm"
                  disabled={oneTimeOffset + ONE_TIME_PAGE_SIZE >= oneTimeTotal || loadingOneTime}
                  onClick={() => loadOneTimePage(oneTimeOffset + ONE_TIME_PAGE_SIZE, oneTimeFilter)}
                >
                  Next →
                </button>
              </div>
            )}
          </>
        )}

        {showAllSubscribers && activeSubscribers.length > 0 && (
          <>
            <div className="stripe-label stripe-text-muted" style={{ marginTop: '0.5rem', marginBottom: '0.75rem' }}>
              Active Subscribers <span className="stripe-text-dark" style={{ fontWeight: 700 }}>({activeSubscribers.length})</span>
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table className="stripe-table">
                <thead>
                  <tr>
                    <th>Email</th>
                    <th className="d-none d-md-table-cell">Plan</th>
                    <th className="d-none d-md-table-cell">Next Charge</th>
                    <th style={{ width: '1%' }}></th>
                  </tr>
                </thead>
                <tbody>
                  {activeSubscribers.map((user) => (
                    <tr key={user.userid}>
                      <td>
                        <div className="stripe-text-dark" style={{ fontWeight: 600, fontSize: '0.85rem' }}>{user.email}</div>
                        <div className="stripe-text-muted d-md-none" style={{ fontSize: '0.75rem' }}>{user.planName} · {user.planInterval}</div>
                      </td>
                      <td className="d-none d-md-table-cell">
                        <span className="stripe-badge stripe-badge--green">{user.planName || "Unknown"} ({user.planInterval})</span>
                      </td>
                      <td className="d-none d-md-table-cell">{user.nextChargeDate ? formatDate(user.nextChargeDate) : "—"}</td>
                      <td style={{ textAlign: 'right' }}>
                        <button className="stripe-btn--sm stripe-btn--sm-primary" onClick={() => handleSelectUser(user.userid)}>View</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        {searchResults.length > 0 && !showAllSubscribers && !showOneTime && (
          <>
            <div className="stripe-label stripe-text-muted" style={{ marginTop: '0.5rem', marginBottom: '0.75rem' }}>
              Results <span className="stripe-text-dark" style={{ fontWeight: 700 }}>({searchResults.length})</span>
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table className="stripe-table">
                <thead>
                  <tr>
                    <th>User</th>
                    <th className="d-none d-md-table-cell">Created</th>
                    <th className="d-none d-sm-table-cell">Balance</th>
                    <th style={{ width: '1%' }}></th>
                  </tr>
                </thead>
                <tbody>
                  {searchResults.map((user) => (
                    <tr key={user.userid}>
                      <td>
                        <div className="stripe-text-dark" style={{ fontWeight: 600, fontSize: '0.85rem' }}>{user.email}</div>
                        <div className="stripe-text-muted" style={{ fontSize: '0.75rem' }}>{user.userid}</div>
                        {user.matched_card && (
                          <div style={{ fontSize: '0.75rem', marginTop: '2px', color: '#635bff', fontFamily: 'monospace', fontWeight: 600 }}>
                            <i className="fas fa-credit-card me-1" style={{ fontSize: '0.65rem' }}></i>
                            {user.matched_card}
                            {user.nmi_match && (
                              <span style={{ marginLeft: '6px', fontSize: '0.6rem', background: '#635bff', color: '#fff', borderRadius: '3px', padding: '1px 5px', fontFamily: 'inherit', verticalAlign: 'middle' }}>NMI</span>
                            )}
                            {/* Says why the number was not on file: a wallet
                                payment never gives us one. */}
                            {user.card_type && (
                              <span style={{ marginLeft: '6px', fontSize: '0.6rem', background: '#eef2ff', color: '#4338ca', borderRadius: '3px', padding: '1px 5px', fontFamily: 'inherit', verticalAlign: 'middle' }}>{user.card_type}</span>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="d-none d-md-table-cell">{formatDate(user.created_at)}</td>
                      <td className="d-none d-sm-table-cell" style={{ fontWeight: 600 }}>${user.balance?.toFixed(2)}</td>
                      <td style={{ textAlign: 'right' }}>
                        <button className="stripe-btn--sm stripe-btn--sm-primary" onClick={() => handleSelectUser(user.userid)}>View</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        {!showAllSubscribers && searchResults.length === 0 && searchQuery && !searching && (
          <div className="stripe-empty-state">No users found matching your search.</div>
        )}
      </div>
    </div>
  );

  // ─── User Details ───────────────────────────────────────────────────────
  const renderUserDetails = () => {
    if (!userDetails) return <div className="stripe-empty-state"><Spinner animation="border" size="sm" /> Loading...</div>;

    const details = [
      { label: 'User ID', value: userDetails.userid },
      { label: 'Email', value: userDetails.email },
      { label: 'Full Name', value: userDetails.fullName || '—' },
      { label: 'Created', value: formatDate(userDetails.created_at) },
      { label: 'Balance', value: `$${userDetails.balance?.toFixed(2)}` },
      { label: 'IP Address', value: userDetails.ip || '—' },
      { label: 'Affiliate', value: userDetails.affiliate || '—' },
      { label: 'Selected Profile', value: userDetails.selectedProfile || '—' },
    ];

    return (
      <>
        <div className="d-flex gap-2 flex-wrap" style={{ marginBottom: '1.25rem' }}>
          <button className="stripe-btn stripe-btn--primary" style={{ padding: '8px 18px', fontSize: '0.85rem' }} onClick={() => setShowEditUserModal(true)}>Edit User</button>
          <button className="stripe-btn stripe-btn--outline" style={{ padding: '8px 18px', fontSize: '0.85rem' }} onClick={handleLoginAsUser}>
            Login as User &#8599;
          </button>
        </div>

        <Row>
          <Col lg={8}>
            <div className="stripe-card--sm" style={{ overflow: 'hidden', marginBottom: '1.5rem' }}>
              <div style={{ padding: '1.25rem 1.5rem' }}>
                <div className="stripe-label stripe-text-muted" style={{ marginBottom: '1rem' }}>Account Information</div>
                {details.map((d, i) => (
                  <div className="stripe-detail-row" key={i}>
                    <span className="stripe-detail-row__label">{d.label}</span>
                    <span className="stripe-detail-row__value">{d.value}</span>
                  </div>
                ))}
              </div>
            </div>
          </Col>
          <Col lg={4}>
            <div className="d-flex flex-column gap-3">
              {[
                { num: userDetails.profileCount, label: 'Profiles' },
                { num: userDetails.activeSubscriptions, label: 'Active Subs' },
                { num: userDetails.boostCount, label: 'Boost Orders' },
              ].map((stat, i) => (
                <div className="stripe-stat-card" key={i}>
                  <div className="stripe-stat-card__number">{stat.num ?? 0}</div>
                  <div className="stripe-stat-card__label">{stat.label}</div>
                </div>
              ))}
            </div>
          </Col>
        </Row>
      </>
    );
  };

  // ─── Profiles ───────────────────────────────────────────────────────────
  const renderUserProfiles = () => (
    <div className="stripe-card--sm" style={{ overflow: 'hidden' }}>
      <div style={{ padding: '1.25rem 1.5rem' }}>
        <div className="stripe-label stripe-text-muted" style={{ marginBottom: '1rem' }}>Profiles</div>
        {userProfiles.length === 0 ? (
          <div className="stripe-empty-state">No profiles found for this user.</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="stripe-table">
              <thead>
                <tr>
                  <th>Platform</th>
                  <th>Username</th>
                  <th className="d-none d-sm-table-cell">Plan</th>
                  <th className="d-none d-md-table-cell">Subscription ID</th>
                  <th className="d-none d-md-table-cell">Created</th>
                  <th className="d-none d-sm-table-cell">Followers</th>
                  <th style={{ width: '1%' }}></th>
                </tr>
              </thead>
              <tbody>
                {userProfiles.map((profile) => (
                  <tr key={profile.profileid}>
                    <td style={{ fontWeight: 600, textTransform: 'capitalize' }}>{profile.platform}</td>
                    <td><span style={{ color: '#635bff', fontWeight: 500 }}>@{profile.username || "—"}</span></td>
                    <td className="d-none d-sm-table-cell">
                      <span className={`stripe-badge stripe-badge--${planColor[profile.planName] || 'gray'}`}>{profile.planName || 'free'}</span>
                    </td>
                    <td className="d-none d-md-table-cell stripe-text-muted" style={{ fontSize: '0.78rem' }}>{profile.subscriptionid || "—"}</td>
                    <td className="d-none d-md-table-cell">{formatDate(profile.created_at || profile.timestamp)}</td>
                    <td className="d-none d-sm-table-cell">{(profile.followers || 0).toLocaleString()}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      <div className="d-flex gap-1">
                        <button className="stripe-btn--sm stripe-btn--sm-outline" onClick={() => handleEditProfile(profile)}>Edit</button>
                        <button className="stripe-btn--sm stripe-btn--sm-danger" onClick={() => handleDeleteProfile(profile.profileid)}>Delete</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );

  // ─── Boosts ─────────────────────────────────────────────────────────────
  const renderUserTopups = () => {
    const balance = userTopups?.balance ?? 0;
    const history = userTopups?.history || [];
    const kindBadge = {
      topup: { label: 'Top-up', bg: '#dcfce7', color: '#166534' },
      admin: { label: 'Admin', bg: '#eef2ff', color: '#4338ca' },
      credit: { label: 'Credit', bg: '#fef9c3', color: '#a16207' },
    };
    return (
      <div className="stripe-card--sm" style={{ overflow: 'hidden' }}>
        <div style={{ padding: '1.25rem 1.5rem' }}>
          <div className="d-flex flex-wrap gap-4 mb-4">
            <div>
              <div className="stripe-label stripe-text-muted">Current Balance</div>
              <div className="stripe-text-dark" style={{ fontWeight: 700, fontSize: '1.4rem' }}>${balance.toFixed(2)}</div>
            </div>
            <div>
              <div className="stripe-label stripe-text-muted">Paid Top-ups</div>
              <div className="stripe-text-dark" style={{ fontWeight: 700, fontSize: '1.4rem' }}>${(userTopups?.paidTotal || 0).toFixed(2)}</div>
            </div>
            <div>
              <div className="stripe-label stripe-text-muted">Credits Issued</div>
              <div className="stripe-text-dark" style={{ fontWeight: 700, fontSize: '1.4rem' }}>${(userTopups?.creditTotal || 0).toFixed(2)}</div>
            </div>
          </div>

          <div style={{ background: '#f6f9fc', border: '1px solid #e3e8ee', borderRadius: 10, padding: '1rem', marginBottom: '1.5rem' }}>
            <div className="stripe-label stripe-text-muted" style={{ marginBottom: '0.75rem' }}>Add or Remove Funds</div>
            <div className="d-flex flex-wrap gap-2 align-items-start">
              <input
                className="stripe-input"
                style={{ maxWidth: 130 }}
                type="number"
                step="0.01"
                placeholder="25.00"
                value={balanceAmount}
                onChange={(e) => setBalanceAmount(e.target.value)}
              />
              <input
                className="stripe-input"
                style={{ flex: 1, minWidth: 200 }}
                type="text"
                placeholder="Reason (e.g. goodwill for late delivery)"
                value={balanceNote}
                onChange={(e) => setBalanceNote(e.target.value)}
              />
              <button
                className="stripe-btn--sm stripe-btn--sm-primary"
                onClick={handleAdjustBalance}
                disabled={savingBalance}
                style={{ padding: '0.5rem 1rem' }}
              >
                {savingBalance ? 'Applying...' : 'Apply'}
              </button>
            </div>
            <div className="stripe-text-muted" style={{ fontSize: '0.75rem', marginTop: '0.5rem' }}>
              A negative amount takes funds off. Limited to $1,000 either way; the reason is stored with the adjustment.
            </div>
            {balanceError && (
              <div style={{ fontSize: '0.8rem', marginTop: '0.5rem', color: '#b91c1c' }}>{balanceError}</div>
            )}
          </div>

          <div className="stripe-label stripe-text-muted" style={{ marginBottom: '1rem' }}>Top-up History</div>
          {history.length === 0 ? (
            <div className="stripe-empty-state">No top-ups or credits for this user.</div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table className="stripe-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Amount</th>
                    <th>Type</th>
                    <th className="d-none d-md-table-cell">Reference</th>
                    <th className="d-none d-sm-table-cell">Note</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((row) => {
                    const badge = kindBadge[row.kind] || kindBadge.credit;
                    return (
                      <tr key={row.id}>
                        <td>{row.date ? formatDate(row.date) : '—'}</td>
                        <td style={{ fontWeight: 600, color: row.amount < 0 ? '#b91c1c' : '#0a2540' }}>
                          {row.amount < 0 ? '-' : ''}${Math.abs(row.amount).toFixed(2)}
                        </td>
                        <td>
                          <span className="stripe-badge" style={{ background: badge.bg, color: badge.color }}>{badge.label}</span>
                        </td>
                        <td className="d-none d-md-table-cell" style={{ fontFamily: 'monospace', fontSize: '0.78rem' }}>{row.reference || '—'}</td>
                        <td className="d-none d-sm-table-cell" style={{ fontSize: '0.8rem' }}>{row.note || '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderUserBoosts = () => (
    <div className="stripe-card--sm" style={{ overflow: 'hidden' }}>
      <div style={{ padding: '1.25rem 1.5rem' }}>
        <div className="stripe-label stripe-text-muted" style={{ marginBottom: '1rem' }}>Boost Orders</div>
        {userBoosts.length === 0 ? (
          <div className="stripe-empty-state">No boost orders found for this user.</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="stripe-table">
              <thead>
                <tr>
                  <th>Type</th>
                  <th className="d-none d-sm-table-cell">Name</th>
                  <th>Qty</th>
                  <th>Cost</th>
                  <th>Status</th>
                  <th className="d-none d-md-table-cell">Link</th>
                  <th className="d-none d-md-table-cell">Created</th>
                </tr>
              </thead>
              <tbody>
                {userBoosts.map((boost) => (
                  <tr key={boost.id}>
                    <td style={{ fontWeight: 600 }}>{boost.boost_type}</td>
                    <td className="d-none d-sm-table-cell" style={{ maxWidth: '120px', overflow: 'hidden', textOverflow: 'ellipsis' }}>{boost.boost_name}</td>
                    <td>{boost.quantity}</td>
                    <td style={{ fontWeight: 600 }}>${boost.cost?.toFixed(2)}</td>
                    <td><span className={`stripe-badge stripe-badge--${statusColor[boost.status] || 'gray'}`}>{boost.status}</span></td>
                    <td className="d-none d-md-table-cell">
                      {boost.link ? (
                        <a href={boost.link} target="_blank" rel="noopener noreferrer" style={{ color: '#635bff', fontSize: '0.82rem' }}>
                          {boost.link.length > 25 ? `${boost.link.substring(0, 25)}...` : boost.link}
                        </a>
                      ) : '—'}
                    </td>
                    <td className="d-none d-md-table-cell">{formatDate(boost.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );

  // ─── Subscriptions ──────────────────────────────────────────────────────
  const renderUserSubscriptions = () => (
    <div className="stripe-card--sm" style={{ overflow: 'hidden' }}>
      <div style={{ padding: '1.25rem 1.5rem' }}>
        <div className="stripe-label stripe-text-muted" style={{ marginBottom: '1rem' }}>Subscriptions</div>
        {userSubscriptions.length === 0 ? (
          <div className="stripe-empty-state">No subscriptions found for this user.</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="stripe-table">
              <thead>
                <tr>
                  <th>Plan</th>
                  <th>Status</th>
                  <th className="d-none d-sm-table-cell">Interval</th>
                  <th className="d-none d-sm-table-cell">Price</th>
                  <th className="d-none d-md-table-cell">Start</th>
                  <th className="d-none d-md-table-cell">Next Charge</th>
                  <th style={{ width: '1%' }}></th>
                </tr>
              </thead>
              <tbody>
                {userSubscriptions.map((sub) => (
                  <tr key={sub.subscriptionid}>
                    <td>
                      <div style={{ fontWeight: 600, textTransform: 'capitalize' }}>{sub.planName}</div>
                      <div className="stripe-text-muted" style={{ fontSize: '0.75rem' }}>{sub.platform}</div>
                    </td>
                    <td><span className={`stripe-badge stripe-badge--${statusColor[sub.status] || 'gray'}`}>{sub.status}</span></td>
                    <td className="d-none d-sm-table-cell">{sub.planInterval}</td>
                    <td className="d-none d-sm-table-cell" style={{ fontWeight: 600 }}>${sub.price?.toFixed(2)}</td>
                    <td className="d-none d-md-table-cell">{formatDate(sub.startDate)}</td>
                    <td className="d-none d-md-table-cell">{sub.nextChargeDate ? formatDate(sub.nextChargeDate) : "—"}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      <div className="d-flex gap-1 flex-wrap">
                        {sub.status === "active" && (
                          <>
                            <button className="stripe-btn--sm stripe-btn--sm-warning" onClick={() => handleUpdateSubscriptionStatus(sub.subscriptionid, "paused")}>Pause</button>
                            <button className="stripe-btn--sm stripe-btn--sm-danger" onClick={() => handleUpdateSubscriptionStatus(sub.subscriptionid, "cancelled")}>Cancel</button>
                          </>
                        )}
                        {sub.status === "paused" && (
                          <button className="stripe-btn--sm stripe-btn--sm-success" onClick={() => handleUpdateSubscriptionStatus(sub.subscriptionid, "active")}>Resume</button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );

  // ─── Edit User Modal ────────────────────────────────────────────────────
  const renderEditUserModal = () => (
    <Modal show={showEditUserModal} onHide={() => setShowEditUserModal(false)} centered>
      <div style={{ padding: '1.5rem' }}>
        <div className="stripe-label" style={{ marginBottom: '1.25rem', color: '#1a1f36', fontSize: '0.85rem' }}>Edit User</div>
        {[
          { label: 'Email', key: 'email', type: 'email' },
          { label: 'Full Name', key: 'fullName' },
          { label: 'Balance', key: 'balance', type: 'number', step: '0.01' },
          { label: 'Affiliate', key: 'affiliate' },
        ].map((field) => (
          <div key={field.key} style={{ marginBottom: '1rem' }}>
            <label className="stripe-detail-row__label">{field.label}</label>
            <input
              className="stripe-input"
              type={field.type || 'text'}
              step={field.step}
              value={editUserForm[field.key]}
              onChange={(e) => setEditUserForm({ ...editUserForm, [field.key]: field.type === 'number' ? parseFloat(e.target.value) : e.target.value })}
              style={{ marginTop: '4px' }}
            />
            {field.key === 'email' && (
              <button className="stripe-btn--sm stripe-btn--sm-danger" style={{ marginTop: '8px' }}
                onClick={() => setEditUserForm({ ...editUserForm, email: `deleted-${editUserForm.email}` })}
              >Delete User</button>
            )}
          </div>
        ))}
        <div className="d-flex gap-2 justify-content-end" style={{ marginTop: '0.5rem' }}>
          <button className="stripe-btn stripe-btn--outline" style={{ padding: '8px 18px', fontSize: '0.85rem' }} onClick={() => setShowEditUserModal(false)}>Cancel</button>
          <button className="stripe-btn stripe-btn--primary" style={{ padding: '8px 18px', fontSize: '0.85rem' }} onClick={handleSaveUserChanges}>Save Changes</button>
        </div>
      </div>
    </Modal>
  );

  // ─── Edit Profile Modal ─────────────────────────────────────────────────
  const renderEditProfileModal = () => (
    <Modal show={showEditProfileModal} onHide={() => setShowEditProfileModal(false)} centered>
      <div style={{ padding: '1.5rem' }}>
        <div className="stripe-label" style={{ marginBottom: '1.25rem', color: '#1a1f36', fontSize: '0.85rem' }}>Edit Profile</div>
        {[
          { label: 'Platform', key: 'platform', type: 'select', options: [{ v: 'instagram', l: 'Instagram' }, { v: 'tiktok', l: 'TikTok' }, { v: 'youtube', l: 'YouTube' }, { v: 'twitter', l: 'Twitter' }] },
          { label: 'Username', key: 'username', prefix: '@', hint: editProfileForm.platform === 'youtube' ? 'Enter @username not channel name' : null },
          { label: 'Plan Name', key: 'planName', type: 'select', options: [{ v: '', l: 'Free' }, { v: 'basic', l: 'Basic' }, { v: 'pro', l: 'Pro' }] },
          { label: 'Interests', key: 'interests' },
          { label: 'Age Range', key: 'ageRange' },
          { label: 'Gender Target', key: 'genderTarget' },
          { label: 'Countries', key: 'countries' },
        ].map((field) => (
          <div key={field.key} style={{ marginBottom: '1rem' }}>
            <label className="stripe-detail-row__label">
              {field.label}
              {field.hint && <span className="stripe-text-muted" style={{ fontWeight: 400, marginLeft: '6px', fontSize: '0.72rem' }}>{field.hint}</span>}
            </label>
            {field.type === 'select' ? (
              <select
                className="stripe-select"
                value={editProfileForm[field.key]}
                onChange={(e) => setEditProfileForm({ ...editProfileForm, [field.key]: e.target.value })}
                style={{ marginTop: '4px' }}
              >
                {field.options.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
              </select>
            ) : (
              <div className="d-flex" style={{ marginTop: '4px' }}>
                {field.prefix && <span className="stripe-input stripe-input--prefix">{field.prefix}</span>}
                <input
                  className={`stripe-input${field.prefix ? ' stripe-input--after-prefix' : ''}`}
                  type="text"
                  value={editProfileForm[field.key]}
                  onChange={(e) => setEditProfileForm({ ...editProfileForm, [field.key]: e.target.value })}
                />
              </div>
            )}
          </div>
        ))}
        <div className="d-flex gap-2 justify-content-end" style={{ marginTop: '0.5rem' }}>
          <button className="stripe-btn stripe-btn--outline" style={{ padding: '8px 18px', fontSize: '0.85rem' }} onClick={() => setShowEditProfileModal(false)}>Cancel</button>
          <button className="stripe-btn stripe-btn--primary" style={{ padding: '8px 18px', fontSize: '0.85rem' }} onClick={handleSaveProfileChanges}>Save Changes</button>
        </div>
      </div>
    </Modal>
  );

  // ─── Main Return ────────────────────────────────────────────────────────
  return (
    <Container fluid className="stripe-container">
      {!selectedUser ? (
        renderUserSearch()
      ) : (
        <>
          <button className="stripe-back-btn" onClick={handleBackToSearch}>
            <span style={{ fontSize: '1rem' }}>&larr;</span> Back to search
          </button>

          {userDetails && (() => {
            const sp = userProfiles.find(p => p.profileid === userDetails.selectedProfile) || userProfiles[0];
            const hasUsername = sp?.username;
            const rawPhoto = sp?.photo;
            const photoUrl = rawPhoto
              ? (rawPhoto.startsWith('http') ? 'https://rizzup.net/proxy/' + encodeURIComponent(rawPhoto) : rawPhoto)
              : null;
            const initial = (hasUsername ? sp.username : userDetails.email || '?')[0].toUpperCase();
            return (
              <div className="stripe-user-header">
                {photoUrl ? (
                  <img
                    src={photoUrl}
                    alt=""
                    style={{ width: 40, height: 40, borderRadius: 10, objectFit: 'cover' }}
                    onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                  />
                ) : null}
                <div className="stripe-user-header__avatar" style={photoUrl ? { display: 'none' } : {}}>
                  {initial}
                </div>
                <div style={{ flex: 1 }}>
                  <div className="stripe-user-header__name">
                    {hasUsername ? `@${sp.username}` : (userDetails.fullName || userDetails.email)}
                  </div>
                  <div className="stripe-user-header__email">
                    {hasUsername ? userDetails.email : (userDetails.fullName ? userDetails.email : userDetails.userid)}
                  </div>
                </div>
                <span className="stripe-user-header__id">{userDetails.userid}</span>
              </div>
            );
          })()}

          <div className="stripe-admin-tabs">
            {[
              { key: 'details', label: 'Details' },
              { key: 'profiles', label: 'Profiles' },
              { key: 'boosts', label: 'Boosts' },
              { key: 'subscriptions', label: 'Subscriptions' },
              { key: 'topups', label: 'TopUps' },
            ].map(tab => (
              <button
                key={tab.key}
                className={`stripe-admin-tab${activeTab === tab.key ? ' stripe-admin-tab--active' : ''}`}
                onClick={() => setActiveTab(tab.key)}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {activeTab === 'details' && renderUserDetails()}
          {activeTab === 'profiles' && renderUserProfiles()}
          {activeTab === 'boosts' && renderUserBoosts()}
          {activeTab === 'subscriptions' && renderUserSubscriptions()}
          {activeTab === 'topups' && renderUserTopups()}

          {renderEditUserModal()}
          {renderEditProfileModal()}
        </>
      )}
    </Container>
  );
};

export default UserManagement;
